/**
 * Zustand Storage Adapter for IndexedDB
 * 
 * This adapter allows Zustand's persist middleware to use IndexedDB instead of localStorage.
 * IndexedDB is more resilient - it survives browser "clear cookies and site data" actions.
 * 
 * Usage:
 * ```typescript
 * import { createIndexedDBStorage } from '@/services/storage/zustandIndexedDBStorage';
 * 
 * const store = create(
 *   persist(
 *     (set) => ({ ... }),
 *     {
 *       name: 'my-store-key',
 *       storage: createIndexedDBStorage(),
 *     }
 *   )
 * );
 * ```
 */

import type { StateStorage } from 'zustand/middleware';
import { IndexedDBService } from './IndexedDBService';

const STORE_NAME = 'user_preferences';

interface PreferenceItem {
  key: string;
  value: string;
  updatedAt: number;
}

/**
 * Creates a Zustand-compatible StateStorage that persists to IndexedDB.
 * Falls back to localStorage if IndexedDB is unavailable.
 */
export function createIndexedDBStorage(): StateStorage {
  // Check if IndexedDB is available
  const isIndexedDBAvailable = typeof indexedDB !== 'undefined';

  if (!isIndexedDBAvailable) {
    console.warn('⚠️ IndexedDB not available, falling back to localStorage');
    return {
      getItem: (name: string) => localStorage.getItem(name),
      setItem: (name: string, value: string) => localStorage.setItem(name, value),
      removeItem: (name: string) => localStorage.removeItem(name),
    };
  }

  return {
    getItem: async (name: string): Promise<string | null> => {
      try {
        const item = await IndexedDBService.get<PreferenceItem>(STORE_NAME, name);
        return item?.value ?? null;
      } catch (error) {
        console.error(`❌ Error reading ${name} from IndexedDB:`, error);
        // Fallback to localStorage if IndexedDB fails
        return localStorage.getItem(name);
      }
    },

    setItem: async (name: string, value: string): Promise<void> => {
      try {
        const item: PreferenceItem = {
          key: name,
          value,
          updatedAt: Date.now(),
        };
        await IndexedDBService.update(STORE_NAME, item);
      } catch (error) {
        console.error(`❌ Error writing ${name} to IndexedDB:`, error);
        // Fallback to localStorage if IndexedDB fails
        localStorage.setItem(name, value);
      }
    },

    removeItem: async (name: string): Promise<void> => {
      try {
        await IndexedDBService.delete(STORE_NAME, name);
      } catch (error) {
        console.error(`❌ Error removing ${name} from IndexedDB:`, error);
        localStorage.removeItem(name);
      }
    },
  };
}

/**
 * Migrates data from localStorage to IndexedDB for a specific key.
 * Call this during app initialization to migrate existing data.
 */
export async function migrateFromLocalStorage(key: string): Promise<boolean> {
  try {
    const localStorageData = localStorage.getItem(key);
    
    if (!localStorageData) {
      return false; // Nothing to migrate
    }

    // Check if data already exists in IndexedDB
    const existingData = await IndexedDBService.get<PreferenceItem>(STORE_NAME, key);
    
    if (existingData) {
      // Data already exists in IndexedDB, only migrate if localStorage is newer
      // For safety, don't overwrite IndexedDB data
      console.log(`📋 ${key}: Data already exists in IndexedDB, skipping migration`);
      // Clean up localStorage since IndexedDB has the data
      localStorage.removeItem(key);
      return false;
    }

    // Migrate to IndexedDB
    const item: PreferenceItem = {
      key,
      value: localStorageData,
      updatedAt: Date.now(),
    };
    
    await IndexedDBService.update(STORE_NAME, item);
    console.log(`✅ ${key}: Successfully migrated from localStorage to IndexedDB`);
    
    // Remove from localStorage after successful migration
    localStorage.removeItem(key);
    
    return true;
  } catch (error) {
    console.error(`❌ Error migrating ${key} from localStorage to IndexedDB:`, error);
    return false;
  }
}

/**
 * Migrates all Zustand persist keys from localStorage to IndexedDB.
 * Call this once during app initialization.
 */
export async function migrateAllZustandStores(): Promise<void> {
  const keysToMigrate = [
    'wanzo-dashboard-widgets',
    // Add other Zustand persist keys here as needed
  ];

  console.log('📋 Starting migration of Zustand stores to IndexedDB...');
  
  for (const key of keysToMigrate) {
    try {
      const migrated = await migrateFromLocalStorage(key);
      if (migrated) {
        console.log(`✅ Migrated: ${key}`);
      }
    } catch (error) {
      console.error(`❌ Failed to migrate ${key}:`, error);
    }
  }
  
  console.log('📋 Zustand stores migration complete');
}
